import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';
import type { AdminTransaction, AdminTransactionQuery, PaginatedResponse } from '../../services/api';

export interface TransactionsState {
    data: AdminTransaction[];
    total: number;
    page: number;
    limit: number;
    isLoading: boolean;
    error: string | null;
    activeReport: 'all' | 'card-activity';
}

const initialState: TransactionsState = {
    data: [],
    total: 0,
    page: 1,
    limit: 50,
    isLoading: false,
    error: null,
    activeReport: 'all',
};

export const fetchAdminTransactions = createAsyncThunk(
    'transactions/fetchAdmin',
    async (query: AdminTransactionQuery, { rejectWithValue }) => {
        try {
            const response: PaginatedResponse<AdminTransaction> = await apiService.getAdminTransactions(query);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch transactions');
        }
    }
);

export const fetchCardActivity = createAsyncThunk(
    'transactions/fetchCardActivity',
    async (query: AdminTransactionQuery, { rejectWithValue }) => {
        try {
            const response: PaginatedResponse<AdminTransaction> = await apiService.getCardActivity(query);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch card activity');
        }
    }
);

export const uploadTransactionsCsv = createAsyncThunk(
    'transactions/uploadCsv',
    async (file: File, { rejectWithValue }) => {
        try {
            const response = await apiService.uploadTransactionsCsv(file);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to upload CSV');
        }
    }
);

const transactionsSlice = createSlice({
    name: 'transactions',
    initialState,
    reducers: {
        setPagination: (state, action: { payload: { page?: number; limit?: number } }) => {
            if (action.payload.page) state.page = action.payload.page;
            if (action.payload.limit) state.limit = action.payload.limit;
        },
        clearTransactionsError: (state) => {
            state.error = null;
        },
        setActiveReport: (state, action: { payload: 'all' | 'card-activity' }) => {
            state.activeReport = action.payload;
            state.page = 1; // Reset to page 1 when switching reports
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAdminTransactions.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAdminTransactions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.data = action.payload.data;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.limit = action.payload.limit;
            })
            .addCase(fetchAdminTransactions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Card Activity
            .addCase(fetchCardActivity.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCardActivity.fulfilled, (state, action) => {
                state.isLoading = false;
                state.data = action.payload.data;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.limit = action.payload.limit;
            })
            .addCase(fetchCardActivity.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Upload CSV
            .addCase(uploadTransactionsCsv.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(uploadTransactionsCsv.fulfilled, (state) => {
                state.isLoading = false;
                // We might want to refresh the transactions after upload
                state.error = null;
            })
            .addCase(uploadTransactionsCsv.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setPagination, clearTransactionsError, setActiveReport } = transactionsSlice.actions;

export const selectTransactions = (state: { transactions: TransactionsState }) => state.transactions.data;
export const selectTransactionsTotal = (state: { transactions: TransactionsState }) => state.transactions.total;
export const selectTransactionsLoading = (state: { transactions: TransactionsState }) => state.transactions.isLoading;
export const selectTransactionsError = (state: { transactions: TransactionsState }) => state.transactions.error;
export const selectTransactionsPagination = (state: { transactions: TransactionsState }) => ({
    page: state.transactions.page,
    limit: state.transactions.limit,
});
export const selectActiveReport = (state: { transactions: TransactionsState }) => state.transactions.activeReport;

export default transactionsSlice.reducer;
